import { useMemo } from 'react';
import { useBranches } from '../../../hooks/useBranches';
import { resolveBranchRef } from '../../../utils/branchResolver';

const DAY_NAMES = [
  'Sunday',
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
];

const parseScheduleStart = (timeSlot) => {
  if (!timeSlot) return null;
  const match = String(timeSlot).match(/(\d{1,2})(?::(\d{2}))?\s*(am|pm)?/i);
  if (!match) return null;
  let hours = Number(match[1]);
  const minutes = match[2] ? Number(match[2]) : 0;
  const meridiem = match[3]?.toLowerCase();
  if (meridiem === 'pm' && hours < 12) hours += 12;
  if (meridiem === 'am' && hours === 12) hours = 0;
  const date = new Date();
  date.setHours(hours, minutes, 0, 0);
  return date;
};

const isToday = (iso) => {
  if (!iso) return false;
  const d = new Date(iso);
  const now = new Date();
  return (
    d.getFullYear() === now.getFullYear() &&
    d.getMonth() === now.getMonth() &&
    d.getDate() === now.getDate()
  );
};

const TodayRoster = ({
  directory = [],
  enrollments = [],
  courses = {},
  attendanceToday = {},
  onMark,
}) => {
  const { byId: branchesById, bySlug: branchesBySlug } = useBranches();
  const todayDayName = DAY_NAMES[new Date().getDay()];

  const items = useMemo(() => {
    const list = [];
    const directoryById = new Map(directory.map((item) => [item.id, item]));

    enrollments
      .filter((enrollment) => enrollment.status === 'active')
      .forEach((enrollment) => {
        const course = courses[enrollment.courseId];
        if (!course) return;
        const batch = (course.batches || []).find(
          (item, index) =>
            String(item.batchId) === String(enrollment.batchId) ||
            String(index) === String(enrollment.batchId),
        );
        const days = batch?.schedule?.days || [];
        if (!days.includes(todayDayName)) return;
        const student = directoryById.get(enrollment.studentId);
        if (!student) return;

        const scheduleTime = batch?.schedule?.timeSlot || '';
        const scheduleStart = parseScheduleStart(scheduleTime);
        const attendanceList = attendanceToday[enrollment.studentId] || [];
        const alreadyMarked = attendanceList.some(
          (record) =>
            record.courseId === enrollment.courseId &&
            String(record.batchId || '') === String(enrollment.batchId || '') &&
            isToday(record.markedAt || record.date),
        );

        const now = new Date();
        let status = 'expected';
        if (alreadyMarked) status = 'present';
        else if (scheduleStart && now > new Date(scheduleStart.getTime() + 15 * 60000)) {
          status = 'late';
        }

        const resolvedBranch = batch
          ? resolveBranchRef(batch, { byId: branchesById, bySlug: branchesBySlug })
          : resolveBranchRef(course.branch, { byId: branchesById, bySlug: branchesBySlug });
        list.push({
          key: `${enrollment.studentId}-${enrollment.courseId}-${enrollment.batchId || ''}`,
          student,
          course: {
            courseId: enrollment.courseId,
            courseName: course.name || 'Course',
            batchId: enrollment.batchId,
            batchName: batch?.batchId || enrollment.batchId || '',
            branch: resolvedBranch?.name || '',
            scheduleDays: days,
            scheduleTime,
          },
          status,
          scheduleStart,
        });
      });

    list.sort((a, b) => {
      if (a.scheduleStart && b.scheduleStart) {
        return a.scheduleStart - b.scheduleStart;
      }
      return a.student.displayName.localeCompare(b.student.displayName);
    });

    return list;
  }, [
    directory,
    enrollments,
    courses,
    todayDayName,
    attendanceToday,
    branchesById,
    branchesBySlug,
  ]);

  if (!items.length) {
    return (
      <div className="rounded-2xl border border-dashed border-stone-300 bg-white px-4 py-10 text-center text-sm text-stone-600">
        No classes scheduled for {todayDayName}. Enjoy the quiet!
      </div>
    );
  }

  const counts = items.reduce(
    (acc, item) => {
      acc[item.status] = (acc[item.status] || 0) + 1;
      return acc;
    },
    { expected: 0, present: 0, late: 0 },
  );

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-3 gap-2">
        <StatChip label="Expected" value={items.length} tone="neutral" />
        <StatChip label="Present" value={counts.present || 0} tone="success" />
        <StatChip label="Late / no show" value={counts.late || 0} tone="urgent" />
      </div>
      <div className="space-y-2">
        {items.map((item) => (
          <button
            key={item.key}
            type="button"
            onClick={() => onMark?.(item)}
            className={`w-full rounded-2xl border px-4 py-3 text-left transition active:scale-[0.99] ${
              item.status === 'present'
                ? 'border-emerald-200 bg-emerald-50'
                : item.status === 'late'
                  ? 'border-red-200 bg-red-50'
                  : 'border-stone-200 bg-white hover:border-stone-300'
            }`}
          >
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="truncate text-base font-semibold text-stone-950">
                  {item.student.displayName}
                </p>
                <p className="truncate text-xs text-stone-600">
                  {item.course.courseName} · {item.course.batchName || '—'}
                </p>
                <p className="mt-0.5 text-xs text-stone-500">
                  {item.course.scheduleTime || 'Time not set'} ·{' '}
                  {item.course.branch || 'Branch not set'}
                </p>
              </div>
              <Chip status={item.status} />
            </div>
          </button>
        ))}
      </div>
    </div>
  );
};

const StatChip = ({ label, value, tone }) => {
  const tones = {
    neutral: 'bg-stone-100 text-stone-800',
    success: 'bg-emerald-100 text-emerald-800',
    urgent: 'bg-red-100 text-red-800',
  };
  return (
    <div className={`rounded-2xl px-3 py-3 text-center ${tones[tone] || tones.neutral}`}>
      <p className="text-xs font-semibold uppercase tracking-wide">{label}</p>
      <p className="mt-0.5 text-xl font-semibold">{value}</p>
    </div>
  );
};

const Chip = ({ status }) => {
  const map = {
    present: { label: 'Present', className: 'bg-emerald-500 text-white' },
    late: { label: 'Late', className: 'bg-red-500 text-white' },
    expected: { label: 'Expected', className: 'bg-stone-800 text-white' },
  };
  const config = map[status] || map.expected;
  return (
    <span className={`rounded-full px-3 py-1 text-[11px] font-semibold ${config.className}`}>
      {config.label}
    </span>
  );
};

export default TodayRoster;
